import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from '../axiosInterceptor';

export interface CreateFeedbackPayload {
  userID: string;
  rating: number;
  text: string;
}

export interface SearchFeedbackPayload {
  role?: string;
  search?: string;
}

export interface Feedback {
  id: string;
  reviewer_id: string;
  user_id: string;
  rating: number;
  text: string;
  created_at: string;
}

export interface FeedbackWithDetails extends Feedback {
  reviewer_name?: string;
  user_name?: string;
  reviewer_email?: string;
  user_email?: string;
  reviewer_avatar?: string;
  user_avatar?: string;
  reviewer_phone?: string;
  user_phone?: string;
  reviewer_role?: string;
  user_role?: string;
  user_about?: string;
  user_location?: string;
  user_timezone?: string;
}

interface FeedbackState {
  feedbacks: FeedbackWithDetails[];
  currentFeedback: Feedback | null;
  loading: boolean;
  error: string | null;
  createSuccess: boolean;
}

const initialState: FeedbackState = {
  feedbacks: [],
  currentFeedback: null,
  loading: false,
  error: null,
  createSuccess: false,
};

// Create feedback
export const createFeedback = createAsyncThunk(
  'feedback/create',
  async (payload: CreateFeedbackPayload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/feedback', payload);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create feedback');
    }
  }
);

// Search feedback (admin only)
export const searchFeedback = createAsyncThunk(
  'feedback/search',
  async (payload: SearchFeedbackPayload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/feedback/search', payload);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to search feedback');
    }
  }
);

const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    resetCreateSuccess: (state) => {
      state.createSuccess = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetFeedback: (state) => {
      state.loading = false;
      state.feedbacks = [];
      state.currentFeedback = null;
      state.error = null;
      state.createSuccess = false;
    },
  },
  extraReducers: (builder) => {
    // Create feedback
    builder
      .addCase(createFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.createSuccess = false;
      })
      .addCase(createFeedback.fulfilled, (state, action: PayloadAction<Feedback>) => {
        state.loading = false;
        state.createSuccess = true;
        state.currentFeedback = action.payload;
      })
      .addCase(createFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.createSuccess = false;
      });

    // Search feedback
    builder
      .addCase(searchFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchFeedback.fulfilled, (state, action: PayloadAction<FeedbackWithDetails[]>) => {
        state.loading = false;
        state.feedbacks = action.payload;
      })
      .addCase(searchFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetCreateSuccess, clearError, resetFeedback } = feedbackSlice.actions;
export default feedbackSlice.reducer;
